import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { useRouterState } from '@tanstack/react-router';
import { ReactNode, useLayoutEffect, useRef } from 'react';
import { ErrorCard } from './ErrorCard.tsx';
import { interpretError } from './errors.tsx';

interface Props {
  children: ReactNode;
  isRoot?: boolean;
}

export function ErrorBoundary(props: Props) {
  const pathname = useRouterState({ select: (state) => state.resolvedLocation.pathname });
  const boundaryRef = useRef<SentryErrorBoundary>(null);
  const previousPathnameRef = useRef(pathname);
  const hasErrorRef = useRef(false);

  // Reset captured errors after navigation without remounting healthy route content.
  useLayoutEffect(() => {
    if (previousPathnameRef.current === pathname) return;

    previousPathnameRef.current = pathname;
    if (!hasErrorRef.current) return;

    hasErrorRef.current = false;
    boundaryRef.current?.resetErrorBoundary();
  }, [pathname]);

  return (
    <SentryErrorBoundary
      ref={boundaryRef}
      {...props}
      onError={(error) => {
        hasErrorRef.current = true;
        const { shouldBeHandledAtRoot } = interpretError(error);
        if (shouldBeHandledAtRoot && !props.isRoot) {
          throw error;
        }
      }}
      onReset={() => {
        hasErrorRef.current = false;
      }}
      fallback={({ error, eventId }) => <ErrorCard {...interpretError(error)} eventId={eventId} />}
    />
  );
}
